import { Section, CodeBlock, InfoBox } from "@/components"

export function NextTailwindStatePage() {
  return (
    <div className="space-y-10">
      <div className="space-y-4">
        <h1 className="text-4xl font-bold">State Management</h1>
        <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
          Learn about client-side state management in Next.js 14. We use Zustand for lightweight 
          global state, complementing Next.js's server-side data fetching.
        </p>
      </div>

      <Section title="State in Next.js" description="Server vs Client state">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            Next.js 14 encourages server-side data fetching for most data needs. Use client-side 
            state management only for UI state and data that must be interactive.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-card border rounded-lg p-6 space-y-3">
              <h3 className="font-semibold">Server State</h3>
              <p className="text-sm text-muted-foreground">
                Data fetched in Server Components, cached automatically by Next.js
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex gap-2"><span className="text-primary">→</span><span>User profiles</span></li>
                <li className="flex gap-2"><span className="text-primary">→</span><span>Blog posts</span></li>
                <li className="flex gap-2"><span className="text-primary">→</span><span>Product listings</span></li>
              </ul>
            </div>
            
            <div className="bg-card border rounded-lg p-6 space-y-3">
              <h3 className="font-semibold">Client State</h3>
              <p className="text-sm text-muted-foreground">
                Interactive UI state managed with Zustand in Client Components
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex gap-2"><span className="text-primary">→</span><span>Theme preference</span></li>
                <li className="flex gap-2"><span className="text-primary">→</span><span>Sidebar state</span></li>
                <li className="flex gap-2"><span className="text-primary">→</span><span>Modal visibility</span></li>
              </ul>
            </div>
          </div>
        </div>
      </Section>

      <Section title="Zustand for Client State" description="Lightweight stores">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            The template uses Zustand for client-side state. Zustand stores work only in 
            Client Components.
          </p>
          
          <CodeBlock
            code={`// store/theme.store.ts
"use client"

import { create } from "zustand"
import { persist } from "zustand/middleware"

type Theme = "light" | "dark" | "system"

interface ThemeState {
  theme: Theme
  setTheme: (theme: Theme) => void
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      theme: "system",
      setTheme: (theme) => {
        set({ theme })
        
        // Apply theme to document
        const root = document.documentElement
        root.classList.remove("light", "dark")
        
        if (theme === "system") {
          const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches
            ? "dark"
            : "light"
          root.classList.add(systemTheme)
        } else {
          root.classList.add(theme)
        }
      }
    }),
    {
      name: "theme-storage"
    }
  )
)`}
            language="typescript"
            filename="store/theme.store.ts"
          />

          <div className="mt-6 space-y-4">
            <h3 className="text-lg font-semibold">Using in Client Components</h3>
            <CodeBlock
              code={`// components/ThemeSwitcher.tsx
"use client"

import { useThemeStore } from "@/store/theme.store"
import { Sun, Moon, Monitor } from "lucide-react"

export function ThemeSwitcher() {
  const { theme, setTheme } = useThemeStore()

  return (
    <div className="flex gap-2">
      <button
        onClick={() => setTheme("light")}
        className={\`p-2 rounded-lg transition-colors \${
          theme === "light" 
            ? "bg-blue-600 text-white" 
            : "bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600"
        }\`}
      >
        <Sun className="h-5 w-5" />
      </button>
      <button
        onClick={() => setTheme("dark")}
        className={\`p-2 rounded-lg transition-colors \${
          theme === "dark" 
            ? "bg-blue-600 text-white" 
            : "bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600"
        }\`}
      >
        <Moon className="h-5 w-5" />
      </button>
      <button
        onClick={() => setTheme("system")}
        className={\`p-2 rounded-lg transition-colors \${
          theme === "system" 
            ? "bg-blue-600 text-white" 
            : "bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600"
        }\`}
      >
        <Monitor className="h-5 w-5" />
      </button>
    </div>
  )
}`}
              language="typescript"
            />
          </div>

          <InfoBox type="warning" title="Client Components Only">
            Zustand stores can only be used in Client Components. Add <code className="bg-muted px-2 py-1 rounded">"use client"</code> 
            to any component using stores.
          </InfoBox>
        </div>
      </Section>

      <Section title="Server-Side Data Fetching" description="Preferred approach">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            For most data, use Server Components to fetch directly without client-side state.
          </p>
          
          <CodeBlock
            code={`// app/dashboard/page.tsx (Server Component)

async function getDashboardData() {
  const res = await fetch('https://api.example.com/dashboard', {
    cache: 'no-store'
  })
  return res.json()
}

export default async function DashboardPage() {
  const data = await getDashboardData()

  return (
    <div className="space-y-8">
      <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
        Dashboard
      </h1>
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border">
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Total Users
          </h3>
          <p className="text-3xl font-bold mt-2">{data.users}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border">
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Revenue
          </h3>
          <p className="text-3xl font-bold mt-2">\${data.revenue}</p>
        </div>
      </div>
    </div>
  )
}

// No client-side state needed!`}
            language="typescript"
          />
        </div>
      </Section>

      <div className="bg-primary/5 border border-primary/20 rounded-xl p-8 space-y-4">
        <h2 className="text-2xl font-bold">Best Practices</h2>
        <ul className="space-y-3 text-sm">
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Prefer Server Components for data fetching - no client state needed</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Use Zustand only for UI state that needs to be global on the client</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Keep stores small and focused on a single domain</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Use persist middleware sparingly - only for user preferences</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>For local component state, use useState - no need for global state</span>
          </li>
        </ul>
      </div>
    </div>
  )
}

